import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, CircleAlert, Info, TriangleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { WEBVIEW_HEADER_HEIGHT_PX } from 'shared/config/layout'
import { getOverlayPortalHost } from 'shared/lib/portalHost'
import { setToastContext, type ToastColor, type ToastOptions } from './toast-bridge'

interface ToastRecord extends Required<
  Pick<ToastOptions, 'title' | 'color' | 'duration' | 'closable'>
> {
  id: string
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const timeoutId = window.setTimeout(() => onDismiss(toast.id), toast.duration)
    return () => window.clearTimeout(timeoutId)
  }, [onDismiss, toast.duration, toast.id])

  const colorClasses: Record<ToastColor, string> = {
    success: 'border-emerald-700 bg-emerald-600',
    error: 'border-red-700 bg-red-600',
    info: 'border-sky-700 bg-sky-600',
    warning: 'border-amber-700 bg-amber-500',
  }

  const iconClasses: Record<ToastColor, string> = {
    success: 'text-emerald-50',
    error: 'text-red-50',
    info: 'text-sky-50',
    warning: 'text-amber-50',
  }

  const icons: Record<ToastColor, React.ReactNode> = {
    success: <CheckCircle2 className="size-4" />,
    error: <CircleAlert className="size-4" />,
    info: <Info className="size-4" />,
    warning: <TriangleAlert className="size-4" />,
  }

  return (
    <div
      className={cn(
        'pointer-events-auto flex min-w-[18rem] max-w-[min(92vw,26rem)] items-start gap-3 rounded-xl border px-4 py-3 text-white transition-[opacity] duration-120 ease-out',
        colorClasses[toast.color]
      )}
      style={{
        contain: 'paint',
      }}
      role={toast.color === 'error' || toast.color === 'warning' ? 'alert' : 'status'}
      aria-live={toast.color === 'error' || toast.color === 'warning' ? 'assertive' : 'polite'}
    >
      <div
        className={cn(
          'mt-0.5 flex h-5 shrink-0 items-center justify-center',
          iconClasses[toast.color]
        )}
      >
        {icons[toast.color]}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="overflow-hidden whitespace-pre-line break-words text-sm font-medium leading-5 text-white"
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          }}
        >
          {toast.title}
        </div>
      </div>
      {toast.closable ? (
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-black/10 hover:text-white"
          aria-label="Закрыть уведомление"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  )
}

export interface ToastProviderProps {
  children: React.ReactNode
}

function ToastViewportHost() {
  const [toasts, setToasts] = useState<ToastRecord[]>([])

  const dismissToast = (id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }

  useEffect(() => {
    setToastContext({
      toast: (options: ToastOptions) => {
        const nextToast: ToastRecord = {
          id: window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
          title: options.title,
          color: options.color,
          duration: options.duration ?? 2200,
          closable: options.closable ?? true,
        }

        setToasts((current) => [...current, nextToast])
      },
    })

    return () => {
      setToastContext(null)
    }
  }, [])

  const portalHost = getOverlayPortalHost()
  if (!portalHost) return null

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-3 px-4"
      style={{ top: WEBVIEW_HEADER_HEIGHT_PX + 8 }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>,
    portalHost
  )
}

export function ToastProvider({ children }: ToastProviderProps) {
  return (
    <>
      {children}
      <ToastViewportHost />
    </>
  )
}
